import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PauseIcon, PlayIcon } from 'lucide-react';
import { IMAGES, VIDEO } from '../data/site';
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
type HeroBackgroundProps = {
  sectionRef: React.RefObject<HTMLElement>;
};
export function HeroBackground({
  sectionRef
}: HeroBackgroundProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion] = useState(prefersReducedMotion);
  const [wantsPlay, setWantsPlay] = useState(!reducedMotion);
  const [mounted, setMounted] = useState(!reducedMotion);
  const [isPlaying, setIsPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [inView, setInView] = useState(true);
  const [tabVisible, setTabVisible] = useState(true);
  useEffect(() => {
    const onVisibility = () => setTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => setInView(entries[0]?.isIntersecting ?? true), {
      threshold: 0.05
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [sectionRef]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video || failed) return;
    const shouldPlay = wantsPlay && inView && tabVisible;
    if (shouldPlay) {
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          // Autoplay refused by the browser — the poster still stands in.
          setIsPlaying(false);
        });
      }
    } else {
      video.pause();
    }
  }, [wantsPlay, inView, tabVisible, mounted, failed]);
  const toggle = useCallback(() => {
    setMounted(true);
    setWantsPlay(current => !current);
  }, []);
  return <>
      <img src={IMAGES.heroMetalPoster} width={1536} height={672} alt="" aria-hidden="true" fetchPriority="high" className="absolute inset-0 -z-20 h-full w-full object-cover object-center" data-id="element-192" data-mp="v124c15xlubz" />

      {mounted && !failed && <video ref={videoRef} aria-hidden="true" tabIndex={-1} muted loop playsInline preload="auto" poster={IMAGES.heroMetalPoster} width={1536} height={672} onPlaying={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onError={() => {
      setFailed(true);
      setIsPlaying(false);
    }} className="absolute inset-0 -z-10 h-full w-full object-cover object-center" data-id="element-193" data-mp="1iqd71o6udysr">
          <source src={VIDEO.heroMetalShaping} type="video/mp4" data-id="element-194" data-mp="3ompnowusuc1p" />
        </video>}

      <div aria-hidden="true" className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,rgba(15,16,17,0.90)_0%,rgba(15,16,17,0.72)_50%,rgba(15,16,17,0.92)_100%)] lg:bg-[linear-gradient(90deg,rgba(15,16,17,0.85)_0%,rgba(15,16,17,0.78)_30%,rgba(15,16,17,0.25)_58%,rgba(15,16,17,0.55)_100%)]" data-id="element-195" data-mp="240950grea706" />

      <div className="relative mx-auto flex max-w-[1240px] justify-end px-5 pb-3 sm:px-8" data-id="element-196" data-mp="2nqbgp6ongrp3">
        <button type="button" onClick={toggle} className="inline-flex min-h-[44px] items-center gap-2 px-2 text-[12px] text-white/80 transition-colors duration-150 ease-out hover:text-white" data-id="element-197" data-mp="2nsntz6jgqdvt">
          {isPlaying ? <PauseIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-198" data-mp="31ornexmiii78" /> : <PlayIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-199" data-mp="12drs3njttc5s" />}
          {isPlaying ? 'Pause background video' : 'Play background video'}
        </button>
      </div>
    </>;
}